import Schedule from "../Entity/Schedule";
import * as Yup from "yup";
import CrudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "../endpoints";
import listHandler from "@serie3/common/Domain/listHandler";

const createScheduleForm = (
  dataList,
  setDataFunction,
  setAdditionalData,
  additonalDataList,
  postId,
  /** called with the API response after a schedule was created or edited */
  onSaved = null
) => {
  // One request at a time: a second click while the first is in flight would
  // create the schedule twice.
  let inFlight = false;

  const onSubmit = async (formValues, actions) => {
    if (inFlight) return;
    inFlight = true;
    // Work on a copy; Formik's own values object is not ours to mutate.
    const values = { ...formValues, post: { ...(formValues.post ?? {}) } };
    const originalValues = { ...formValues };
    if (values.scheduled_for == "") {
      delete values.scheduled_for;
    }
    const publishAs = Number(values.publish_as) || 0;
    delete values.publish_as;

    // After a successful create, the form holds the new id so that a further
    // Save edits that schedule instead of creating another one.
    const rememberId = (response) => {
      if (response?.ok && response?.data?.id && actions?.setFieldValue) {
        actions.setFieldValue("id", response.data.id, false);
      }
    };

    try {
    if (values.id > 0) {
      let payload = JSON.stringify(values, null, 2);
      const response = await CrudApi.edit(
        SocialEndpoints.posts.schedule.edit,
        payload
      );

      // The API's row, not the form values: the table needs post_status and
      // the publish_as object, which the form does not carry.
      listHandler.updateList(
        response,
        JSON.stringify(response.ok ? response.data : originalValues, null, 2),
        dataList,
        setDataFunction
      );

      if (response.ok && additonalDataList && setAdditionalData) {
        listHandler.replaceItemInList(
          response.data,
          additonalDataList,
          setAdditionalData
        );
      }
      if (response.ok && typeof onSaved === "function") onSaved(response);
    } else if (publishAs > 0) {
      // Plan on another group member's account: the schedule starts as
      // "proposed" and only publishes once that member approved it.
      const targetPostId = typeof postId !== "undefined" ? postId : values.post?.id;
      const response = await CrudApi.createNew(
        SocialEndpoints.posts.schedule.planFor + targetPostId,
        JSON.stringify({
          publish_as: publishAs,
          local_user_time: values.local_user_time,
          timezone: values.timezone,
          platforms: values.platforms ?? ["linkedin"],
        })
      );
      rememberId(response);

      listHandler.newToList(response, dataList, setDataFunction, null);

      additonalDataList &&
        setAdditionalData &&
        listHandler.newToList(
          response,
          additonalDataList,
          setAdditionalData,
          null
        );
      if (response.ok && typeof onSaved === "function") onSaved(response);
    } else {
      if (typeof postId !== "undefined") {
        values.post.id = postId;
      }
      let payload = JSON.stringify(values, null, 2);
      const response = await CrudApi.createNew(
        SocialEndpoints.posts.schedule.new,
        payload
      );
      rememberId(response);

      listHandler.newToList(response, dataList, setDataFunction, null);

      additonalDataList &&
        setAdditionalData &&
        listHandler.newToList(
          response,
          additonalDataList,
          setAdditionalData,
          null
        );
      if (response.ok && typeof onSaved === "function") onSaved(response);
    }
    } finally {
      inFlight = false;
      actions?.setSubmitting?.(false);
    }
  };

  // The date is the one thing the backend cannot guess; say so in the form
  // instead of letting the request fail.
  const validationSchema = Yup.object({
    local_user_time: Yup.string().trim().required("Please pick a date and time"),
    timezone: Yup.string().trim().required("Please pick a timezone"),
  });

  return {
    initialValues: Schedule,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values, actions) => onSubmit(values, actions),
  };
};

const setEditData = (editData) => {
  let Schedule = {
    id: editData.id,
    local_user_time: editData.local_user_time || editData.scheduled_for || "",
    platforms: editData.platforms ?? (editData.platform ? [editData.platform] : ["linkedin"]),
    timezone: editData.timezone,
    publish_as: editData.publish_as?.id ? String(editData.publish_as.id) : "",
    post: { id: editData.post.id },
  };

  return Schedule;
};

export default { createScheduleForm, setEditData };
